/**
 * White-Label API client for resolving branding configuration by domain.
 *
 * Called by the ThemeProvider on mount to fetch entity-specific branding
 * from the White-Label Service. The resolved config drives CSS custom
 * properties injected onto :root and supplies entity context (broker,
 * association, or carrier) to descendant components.
 *
 * @module shared/theme/whitelabel-api
 */

const API_BASE_URL =
  typeof window !== 'undefined'
    ? ((window as unknown as Record<string, unknown>).__IPA_API_BASE_URL__ as string) ?? ''
    : '';

/** Generates a UUID v4 for correlation IDs. */
function uuid(): string {
  return crypto.randomUUID();
}

/**
 * The type of entity whose branding is being resolved.
 * IPA_DEFAULT is returned when no matching entity is found for the domain.
 */
export type WhitelabelEntityType = 'BROKER' | 'ASSOCIATION' | 'CARRIER' | 'IPA_DEFAULT';

/**
 * Branding configuration resolved from the White-Label Service.
 * Returned by `GET /api/v1/whitelabel/resolve?domain={hostname}`.
 */
export interface BrandingConfig {
  /** Type of entity whose branding was resolved. */
  entityType: WhitelabelEntityType;
  /** Unique identifier for the resolved entity. Empty string for IPA_DEFAULT. */
  entityId: string;
  /** Hex color string for the primary brand color (e.g. "#1a73e8"). */
  primaryColor: string;
  /** Hex color string for the secondary brand color. */
  secondaryColor: string;
  /** Hex color string for the accent brand color. */
  accentColor: string;
  /** CSS font-family string for the brand font. */
  fontFamily: string;
  /** URL of the brand logo, or null to use the IPA default. */
  logoUrl: string | null;
  /** Human-readable display name for the entity (e.g. broker name, carrier name). */
  displayName: string;
  /** Optional tagline to display below the name. */
  tagline?: string;

  // --- Broker-specific fields ---
  /** URL of the broker's profile photo, if uploaded. */
  brokerProfilePhotoUrl?: string;
  /** Broker's "About" text content. */
  brokerAbout?: string;
  /** Broker contact email address. */
  brokerContactEmail?: string;
  /** Broker contact phone number. */
  brokerContactPhone?: string;
  /** Referral code attached to enrollment links. */
  brokerReferralCode?: string;
  /** Whether the broker site is active. Disabled sites redirect to the main IPA site. */
  brokerSiteEnabled?: boolean;

  // --- Association-specific fields ---
  /** Association's "About" text content. */
  associationAbout?: string;

  // --- Carrier-specific fields ---
  /** Carrier ID used to filter plans to a single carrier's offerings. */
  carrierId?: string;
}

/** Default IPA branding applied when no entity is found for the domain. */
export const DEFAULT_BRANDING_CONFIG: BrandingConfig = {
  entityType: 'IPA_DEFAULT',
  entityId: '',
  primaryColor: '#1a73e8',
  secondaryColor: '#1558b0',
  accentColor: '#e8f0fe',
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  logoUrl: null,
  displayName: 'IPA Platform',
};

/**
 * Resolves branding configuration for the given domain from the White-Label Service.
 *
 * @param domain - The hostname to resolve (e.g. `window.location.hostname`).
 * @returns Resolved branding config, or the IPA default if resolution fails.
 */
export async function resolveBranding(domain: string): Promise<BrandingConfig> {
  const url = `${API_BASE_URL}/api/v1/whitelabel/resolve?domain=${encodeURIComponent(domain)}`;

  const response = await fetch(url, {
    headers: {
      'X-Correlation-ID': uuid(),
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      // No branding configured for this domain — use IPA defaults.
      return DEFAULT_BRANDING_CONFIG;
    }
    // Surface server errors rather than silently falling back, so operators
    // can detect White-Label Service outages. Callers (ThemeProvider) catch
    // this and fall back to IPA defaults with an error state.
    throw new Error(`White-Label Service error: ${response.status}`);
  }

  return response.json() as Promise<BrandingConfig>;
}
